import { ScriptLoader } from '@bigcommerce/script-loader';

import {
    MissingDataError,
    MissingDataErrorType,
    PaymentMethod,
    PaymentMethodClientUnavailableError,
} from '@bigcommerce/checkout-sdk/payment-integration-api';

import { PayPalSdkNamespace } from './paypal-constants';
import {
    FundingType,
    PayPalFastlaneSdk,
    PayPalGooglePaySdk,
    PayPalHostWindow,
    PayPalInitializationData,
    PayPalMessagesSdk,
    PayPalSDK,
    PayPalSdkComponents,
    PayPalSdkConfig,
} from './paypal-types';
import { getPayPalSdkModule, transformLocaleToPayPalFormat } from './utils';

export default class PayPalSdkScriptLoader {
    private window: PayPalHostWindow;

    constructor(private scriptLoader: ScriptLoader) {
        this.window = window;
    }

    async getPayPalSDK(
        paymentMethod: PaymentMethod<PayPalInitializationData>,
        currencyCode: string,
        storeLanguage: string | undefined,
        initializesOnCheckoutPage?: boolean,
        forceLoad?: boolean,
    ): Promise<PayPalSDK> {
        const namespace = this.isBigCommercePaymentsMethod(paymentMethod.id)
            ? PayPalSdkNamespace.BigCommercePaymentsPayPalSDK
            : PayPalSdkNamespace.PayPal;

        if (!getPayPalSdkModule(namespace) || forceLoad) {
            const paypalSdkScriptConfig = this.getPayPalSdkScriptConfigOrThrow(
                paymentMethod,
                currencyCode,
                storeLanguage,
                initializesOnCheckoutPage,
            );

            await this.loadPayPalSdk(paypalSdkScriptConfig);
        }

        const paypalSdk = getPayPalSdkModule(namespace);

        if (!paypalSdk) {
            throw new PaymentMethodClientUnavailableError();
        }

        return paypalSdk;
    }

    async getPayPalFastlaneSdk(
        paymentMethod: PaymentMethod<PayPalInitializationData>,
        currencyCode: string,
        sessionId: string,
        storeLanguage: string | undefined,
    ): Promise<PayPalFastlaneSdk> {
        if (!this.window.paypalFastlaneSdk) {
            const config = this.getPayPalFastlaneSdkConfiguration(
                paymentMethod,
                currencyCode,
                sessionId,
                storeLanguage,
            );

            await this.loadPayPalSdk(config);

            if (!this.window.paypalFastlaneSdk) {
                throw new PaymentMethodClientUnavailableError();
            }
        }

        return this.window.paypalFastlaneSdk;
    }

    async getPayPalGooglePaySdk(
        paymentMethod: PaymentMethod<PayPalInitializationData>,
        currencyCode: string,
        storeLanguage: string | undefined,
        initializesOnCheckoutPage?: boolean,
        forceLoad?: boolean,
    ): Promise<PayPalGooglePaySdk> {
        if (!this.window.paypalGooglePay || forceLoad) {
            const paypalSdkScriptConfig = this.getPayPalGooglePaySdkScriptConfigOrThrow(
                paymentMethod,
                currencyCode,
                storeLanguage,
                initializesOnCheckoutPage,
            );

            await this.loadPayPalSdk(paypalSdkScriptConfig);
        }

        if (!this.window.paypalGooglePay) {
            throw new PaymentMethodClientUnavailableError();
        }

        return this.window.paypalGooglePay;
    }

    async getPayPalApmsSdk(
        paymentMethod: PaymentMethod<PayPalInitializationData>,
        currencyCode: string,
        storeLanguage: string | undefined,
    ) {
        if (!this.window.paypalApms) {
            const config = this.getPayPalApmSdkConfiguration(
                paymentMethod,
                currencyCode,
                storeLanguage,
            );

            await this.loadPayPalSdk(config);

            if (!this.window.paypalApms) {
                throw new PaymentMethodClientUnavailableError();
            }
        }

        return this.window.paypalApms;
    }

    async getPayPalMessages(
        paymentMethod: PaymentMethod<PayPalInitializationData>,
        currencyCode: string,
        storeLanguage: string | undefined,
    ): Promise<PayPalMessagesSdk> {
        if (!this.window.paypalMessages) {
            const paypalSdkMessagesConfig = this.getPayPalSdkMessagesConfiguration(
                paymentMethod,
                currencyCode,
                storeLanguage,
            );

            await this.loadPayPalSdk(paypalSdkMessagesConfig);
        }

        if (!this.window.paypalMessages) {
            throw new PaymentMethodClientUnavailableError();
        }

        return this.window.paypalMessages;
    }

    private isBigCommercePaymentsMethod(methodId: string): boolean {
        return methodId.startsWith('bigcommerce_payments');
    }

    /**
     *
     *  loadPayPalSdk is a paypal sdk script loader
     *  which loads paypal sdk based on provided configuration
     *
     */
    private async loadPayPalSdk({ options, attributes }: PayPalSdkConfig): Promise<void> {
        const scriptOptions = this.transformConfig<PayPalSdkConfig['options']>(options);
        const scriptAttributes = this.transformConfig<PayPalSdkConfig['attributes']>(attributes);

        const paypalSdkUrl = 'https://www.paypal.com/sdk/js';
        const scriptQuery = new URLSearchParams(scriptOptions).toString();
        const scriptSrc = `${paypalSdkUrl}?${scriptQuery}`;

        await this.scriptLoader.loadScript(scriptSrc, {
            async: true,
            attributes: scriptAttributes,
        });
    }

    /**
     *
     * Configurations section
     *
     */
    private getPayPalSdkScriptConfigOrThrow(
        paymentMethod: PaymentMethod<PayPalInitializationData>,
        currencyCode: string,
        storeLanguage: string | undefined,
        initializesOnCheckoutPage = true,
    ): PayPalSdkConfig {
        const { id, clientToken, initializationData } = paymentMethod;

        if (!initializationData || !initializationData.clientId) {
            throw new MissingDataError(MissingDataErrorType.MissingPaymentMethod);
        }

        const {
            intent,
            clientId,
            merchantId,
            buyerCountry,
            attributionId,
            isVenmoEnabled,
            isHostedCheckoutEnabled,
            isPayPalCreditAvailable,
            isDeveloperModeApplicable,
            availableAlternativePaymentMethods = [],
            enabledAlternativePaymentMethods = [],
            isGooglePayEnabled,
        } = initializationData;

        const commit = isHostedCheckoutEnabled || initializesOnCheckoutPage;

        const shouldEnableCard =
            id === 'paypalcommercecreditcards' || id === 'bigcommerce_payments_creditcards';
        const enableCardFunding = shouldEnableCard ? ['card'] : [];
        const disableCardFunding = !shouldEnableCard ? ['card'] : [];

        const enableCreditFunding = isPayPalCreditAvailable ? ['credit', 'paylater'] : [];
        const disableCreditFunding = !isPayPalCreditAvailable ? ['credit', 'paylater'] : [];

        const enableVenmoFunding = isVenmoEnabled ? ['venmo'] : [];
        const disableVenmoFunding = !isVenmoEnabled ? ['venmo'] : [];
        const disableAPMsFunding = availableAlternativePaymentMethods.filter(
            (apm: string) => !enabledAlternativePaymentMethods.includes(apm),
        );
        const googlePayComponent: PayPalSdkComponents = isGooglePayEnabled ? ['googlepay'] : [];
        const cardFieldsComponent: PayPalSdkComponents = initializesOnCheckoutPage
            ? ['card-fields']
            : [];
        const disableFunding: FundingType = this.filterFundingOptions([
            ...disableCardFunding,
            ...disableCreditFunding,
            ...disableVenmoFunding,
            ...disableAPMsFunding,
        ]);
        const enableFunding: FundingType = this.filterFundingOptions([
            ...enableCardFunding,
            ...enableCreditFunding,
            ...enableVenmoFunding,
            ...enabledAlternativePaymentMethods,
        ]);

        const locale = transformLocaleToPayPalFormat(storeLanguage);

        return {
            options: {
                'client-id': clientId,
                'merchant-id': merchantId,
                'enable-funding': enableFunding.length > 0 ? enableFunding : undefined,
                'disable-funding': disableFunding.length > 0 ? disableFunding : undefined,
                commit,
                components: [
                    'buttons',
                    'hosted-fields',
                    'payment-fields',
                    'legal',
                    ...googlePayComponent,
                    ...cardFieldsComponent,
                ],
                currency: currencyCode,
                intent,
                ...(isDeveloperModeApplicable && { 'buyer-country': buyerCountry }),
                ...(locale && { locale }),
            },
            attributes: {
                'data-partner-attribution-id': attributionId,
                'data-client-token': clientToken,
                ...(this.isBigCommercePaymentsMethod(id) && {
                    'data-namespace': PayPalSdkNamespace.BigCommercePaymentsPayPalSDK,
                }),
            },
        };
    }

    private getPayPalFastlaneSdkConfiguration(
        paymentMethod: PaymentMethod<PayPalInitializationData>,
        currencyCode: string,
        sessionId: string,
        storeLanguage: string | undefined,
    ): PayPalSdkConfig {
        const { clientToken, initializationData } = paymentMethod;

        if (!initializationData || !initializationData.clientId) {
            throw new MissingDataError(MissingDataErrorType.MissingPaymentMethod);
        }

        const { intent, clientId, merchantId, attributionId } = initializationData;

        const locale = transformLocaleToPayPalFormat(storeLanguage);

        return {
            options: {
                'client-id': clientId,
                'merchant-id': merchantId,
                commit: true,
                components: [
                    'fastlane',
                    'buttons',
                    'payment-fields',
                    'hosted-fields',
                    'three-domain-secure',
                ],
                currency: currencyCode,
                ...(locale && { locale }),
                intent,
            },
            attributes: {
                'data-client-metadata-id': sessionId.replace(/-/g, ''),
                'data-namespace': PayPalSdkNamespace.PayPalFastlaneSdk,
                'data-partner-attribution-id': attributionId,
                'data-sdk-client-token': clientToken,
            },
        };
    }

    private getPayPalGooglePaySdkScriptConfigOrThrow(
        paymentMethod: PaymentMethod<PayPalInitializationData>,
        currencyCode: string,
        storeLanguage: string | undefined,
        initializesOnCheckoutPage = true,
    ): PayPalSdkConfig {
        const { clientToken, initializationData } = paymentMethod;

        if (!initializationData || !initializationData.clientId) {
            throw new MissingDataError(MissingDataErrorType.MissingPaymentMethod);
        }

        const {
            intent,
            clientId,
            merchantId,
            buyerCountry,
            attributionId,
            isHostedCheckoutEnabled,
            isDeveloperModeApplicable,
        } = initializationData;

        const commit = isHostedCheckoutEnabled || initializesOnCheckoutPage;

        const locale = transformLocaleToPayPalFormat(storeLanguage);

        return {
            options: {
                'client-id': clientId,
                'merchant-id': merchantId,
                commit,
                components: ['googlepay'],
                currency: currencyCode,
                intent,
                ...(isDeveloperModeApplicable && { 'buyer-country': buyerCountry }),
                ...(locale && { locale }),
            },
            attributes: {
                'data-partner-attribution-id': attributionId,
                'data-client-token': clientToken,
                'data-namespace': PayPalSdkNamespace.PayPalGooglePay,
            },
        };
    }

    private getPayPalApmSdkConfiguration(
        paymentMethod: PaymentMethod<PayPalInitializationData>,
        currencyCode: string,
        storeLanguage: string | undefined,
    ): PayPalSdkConfig {
        const { initializationData } = paymentMethod;

        if (!initializationData || !initializationData.clientId) {
            throw new MissingDataError(MissingDataErrorType.MissingPaymentMethod);
        }

        const {
            intent,
            clientId,
            merchantId,
            buyerCountry,
            attributionId,
            isDeveloperModeApplicable,
            availableAlternativePaymentMethods = [],
            enabledAlternativePaymentMethods = [],
        } = initializationData;

        const enableAPMsFunding = this.filterFundingOptions(enabledAlternativePaymentMethods);
        const disableAPMsFunding = this.filterFundingOptions(
            availableAlternativePaymentMethods.filter(
                (apm: string) => !enabledAlternativePaymentMethods.includes(apm),
            ),
        );

        const locale = transformLocaleToPayPalFormat(storeLanguage);

        return {
            options: {
                'client-id': clientId,
                'merchant-id': merchantId,
                'enable-funding': enableAPMsFunding.length > 0 ? enableAPMsFunding : undefined,
                'disable-funding': disableAPMsFunding.length > 0 ? disableAPMsFunding : undefined,
                commit: true,
                components: ['buttons', 'payment-fields'],
                currency: currencyCode,
                intent,
                ...(isDeveloperModeApplicable && { 'buyer-country': buyerCountry }),
                ...(locale && { locale }),
            },
            attributes: {
                'data-partner-attribution-id': attributionId,
                'data-namespace': PayPalSdkNamespace.PayPalApms,
            },
        };
    }

    private getPayPalSdkMessagesConfiguration(
        paymentMethod: PaymentMethod<PayPalInitializationData>,
        currencyCode: string,
        storeLanguage: string | undefined,
    ): PayPalSdkConfig {
        const { initializationData } = paymentMethod;

        if (!initializationData || !initializationData.clientId) {
            throw new MissingDataError(MissingDataErrorType.MissingPaymentMethod);
        }

        const { clientId, merchantId, attributionId, isDeveloperModeApplicable, buyerCountry } =
            initializationData;

        const locale = transformLocaleToPayPalFormat(storeLanguage);

        return {
            options: {
                'client-id': clientId,
                'merchant-id': merchantId,
                components: ['messages'],
                currency: currencyCode,
                ...(isDeveloperModeApplicable && { 'buyer-country': buyerCountry }),
                ...(locale && { locale }),
            },
            attributes: {
                'data-namespace': PayPalSdkNamespace.PayPalMessages,
                'data-partner-attribution-id': attributionId,
            },
        };
    }

    /**
     *
     * Utils methods
     *
     */
    private filterFundingOptions(fundingOptions: FundingType | undefined): FundingType {
        const unsupportedFundingTypes = ['klarna', 'afterpay'];

        if (!fundingOptions) {
            return [];
        }

        return fundingOptions.filter(
            (fundingOption) => !unsupportedFundingTypes.includes(fundingOption),
        );
    }

    private transformConfig<T extends Record<string, unknown>>(config: T): Record<string, string> {
        let transformedConfig = {};

        const keys = Object.keys(config) as Array<keyof T>;

        keys.forEach((key) => {
            const value = config[key];

            if (
                value === undefined ||
                value === null ||
                value === '' ||
                (Array.isArray(value) && value.length === 0)
            ) {
                return;
            }

            transformedConfig = {
                ...transformedConfig,
                [key]: Array.isArray(value) ? value.join(',') : value,
            };
        });

        return transformedConfig;
    }
}
